import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Phone, MessageSquare, Mail, MapPin, Car, Clock, CalendarDays, ChevronRight, Check, AlertCircle, FileText, Send, TrendingUp, AlertTriangle, Play, LogOut as LeaveIcon, Undo2, CalendarPlus, Wallet } from 'lucide-react';

import { isRunning, STATUS_TEXT } from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';

// ─── Helpers ──────────────────────────────────────────────────────────────
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate();
}
function pad2(n) { return String(n).padStart(2, '0'); }
function isoDate(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }

function toDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (v instanceof Date) return v;
  return new Date(v);
}

function formatTimeOfDay(d) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function daysBetween(later, earlier) {
  return Math.floor((later.getTime() - earlier.getTime()) / (24 * 60 * 60 * 1000));
}

// "Today, May 1" / "Tomorrow, May 2" / "Sat, May 3"
function dayHeading(d, today) {
  const tomorrow = addDays(today, 1);
  const monthDay = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (isSameDay(d, today)) return `Today, ${monthDay}`;
  if (isSameDay(d, tomorrow)) return `Tomorrow, ${monthDay}`;
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  return `${weekday}, ${monthDay}`;
}

function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return raw || '';
}

// STATUS_TEXT comes from lib/job-helpers.js — single source of truth.

// Re-tick every minute so "next-or-in-progress" rolls forward without a manual reload.
function useNowTick(intervalMs = 60_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// ─── Hero card (next-or-in-progress) ──────────────────────────────────────
function NextJobHero({ job, customer, vehicle, now, today, onStart, onLeave, onUndoStart, onResume }) {
  if (!job) {
    return (
      <div className="empty-state">
        <CalendarDays size={32} color="var(--text-3)" strokeWidth={1.5} style={{ marginBottom: 8 }} />
        <p style={{ fontWeight: 600 }}>No upcoming jobs.</p>
        <p className="muted small">
          Open the <Link to="/calendar">calendar</Link> to schedule one.
        </p>
      </div>
    );
  }

  // Two notions of "in progress" co-exist:
  //   1. SCHEDULED in-progress = appointment time is current (existing
  //      logic — left intact so the eyebrow still says "In progress"
  //      when 9:30am-10:30am window is happening even if Brad hasn't
  //      tapped Start yet).
  //   2. WORK in-progress = Brad has actually tapped Start (isRunning).
  // The green border-left accent fires for either.
  const scheduledInProgress = job._start <= now && job._end > now;
  const workInProgress      = isRunning(job);
  const inProgress          = scheduledInProgress || workInProgress;

  // "Late" = today's appointment whose scheduled window has already
  // passed AND Brad hasn't started it yet. Honest signal that he's
  // behind, instead of pretending it's still "next."
  const overdueToday = isSameDay(job._start, today) && job._end <= now && !workInProgress;

  const heading = workInProgress
    ? 'On the job'
    : scheduledInProgress
      ? 'In progress'
      : overdueToday
        ? 'Today — running late'
        : isSameDay(job._start, today)
          ? 'Next — today'
          : isSameDay(job._start, addDays(today, 1))
            ? 'Next — tomorrow'
            : `Next — ${job._start.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}`;

  const phoneDigits = String(customer?.phone || '').replace(/\D/g, '');
  const mapsUrl = customer?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`
    : null;

  return (
    <article
      className={`hero-card ${inProgress ? 'in-progress' : ''}${workInProgress ? ' hero-card-tappable' : ''}`}
      onClick={workInProgress ? onResume : undefined}
      onKeyDown={workInProgress ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onResume(); } } : undefined}
      role={workInProgress ? 'button' : undefined}
      tabIndex={workInProgress ? 0 : undefined}
      aria-label={workInProgress ? 'Resume active job' : undefined}
    >
      <div className="hero-eyebrow">
        <span className={`badge badge-${job.status || 'draft'}`}>{STATUS_TEXT[job.status] || 'Draft'}</span>
        <span className="hero-eyebrow-text">{heading}</span>
        {workInProgress && (
          <span className="hero-resume-hint muted small">Tap card to resume</span>
        )}
      </div>

      <div className="hero-time">
        <Clock size={18} />
        <span>{formatTimeOfDay(job._start)}</span>
        <span className="hero-time-dot">·</span>
        <span className="hero-time-duration">{job._durationMinutes} min</span>
      </div>

      <div className="hero-customer">
        <Link
          to={`/customers/${job.customerId}`}
          className="hero-customer-name"
          onClick={(e) => e.stopPropagation()}
        >
          {customer?.name || '(deleted customer)'}
        </Link>
        {(phoneDigits || customer?.email) && (
          <div className="contact-row">
            {phoneDigits && (
              <div className="contact-line">
                <span className="contact-text mono">{formatPhone(customer.phone)}</span>
                <div className="contact-actions">
                  <a
                    href={`tel:${phoneDigits}`}
                    className={`contact-action ${customer.preferredContact?.includes('phone') ? 'preferred' : ''}`}
                    aria-label={`Call ${customer.name}`}
                    title={customer.preferredContact?.includes('phone') ? 'Call (preferred)' : 'Call'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone size={16} />
                  </a>
                  <a
                    href={`sms:${phoneDigits}`}
                    className={`contact-action ${customer.preferredContact?.includes('text') ? 'preferred' : ''}`}
                    aria-label={`Text ${customer.name}`}
                    title={customer.preferredContact?.includes('text') ? 'Text (preferred)' : 'Text'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MessageSquare size={16} />
                  </a>
                </div>
              </div>
            )}
            {customer?.email && (
              <div className="contact-line">
                <span className="contact-text">{customer.email}</span>
                <div className="contact-actions">
                  <a
                    href={`mailto:${customer.email}`}
                    className={`contact-action ${customer.preferredContact?.includes('email') ? 'preferred' : ''}`}
                    aria-label={`Email ${customer.name}`}
                    title={customer.preferredContact?.includes('email') ? 'Email (preferred)' : 'Email'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail size={16} />
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {vehicle && (
        <div className="hero-meta">
          <Car size={14} />
          <span>{vehicleLabel(vehicle)}</span>
        </div>
      )}

      <p className="hero-description">{job.description}</p>

      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hero-address"
          onClick={(e) => e.stopPropagation()}
        >
          <MapPin size={14} />
          <span>{customer.address}</span>
          <ChevronRight size={14} className="hero-address-chevron" />
        </a>
      )}

      {/* Start / Leave actions — work tracker entry point from Home.
          Only shown for non-paid jobs (terminal jobs don't need either).
          When running, the small Undo button next to Leave clears
          startedAt — for the "oh shoot, didn't mean to tap that"
          case. Trivially reversible (just tap Start again). */}
      {job.status !== 'paid' && (
        <div className="hero-actions">
          {!workInProgress ? (
            <button
              type="button"
              className="btn hero-action-btn start"
              onClick={(e) => { e.stopPropagation(); onStart(); }}
            >
              <Play size={16} strokeWidth={2.5} /> Start job
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn hero-action-btn start"
                onClick={(e) => { e.stopPropagation(); onResume(); }}
              >
                <ChevronRight size={16} strokeWidth={2.5} /> Resume job
              </button>
              <button
                type="button"
                className="btn hero-action-btn leave"
                onClick={(e) => { e.stopPropagation(); onLeave(); }}
              >
                <LeaveIcon size={16} strokeWidth={2.5} /> Leave
              </button>
              <button
                type="button"
                className="hero-undo-btn"
                onClick={(e) => { e.stopPropagation(); onUndoStart(); }}
                title="Undo Start (cancel the work timer)"
                aria-label="Undo Start"
              >
                <Undo2 size={16} />
              </button>
            </>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Quick stats card row — counts, not dollars ──────────────────────────
function HomeStats({ stats, monthEarned, onOpenOutstanding, onOpenEarnings }) {
  return (
    <div className="quick-stats">
      <StatCount
        icon={<Wallet size={11} />}
        label="Paid this month"
        count={`$${Math.round(monthEarned.revenue).toLocaleString()}`}
        unit={`${monthEarned.count} job${monthEarned.count === 1 ? '' : 's'}`}
        onClick={onOpenEarnings}
        clickHint="Earnings"
      />
      <StatCount
        icon={<Check size={11} />}
        label="Paid today"
        count={stats.todayPaid}
        unit={stats.todayPaid === 1 ? 'job' : 'jobs'}
      />
      <StatCount
        icon={<TrendingUp size={11} />}
        label="Paid this week"
        count={stats.weekPaid}
        unit={stats.weekPaid === 1 ? 'job' : 'jobs'}
      />
      <StatCount
        icon={<AlertCircle size={11} />}
        label="Outstanding"
        count={stats.outstanding}
        unit={stats.outstanding === 1 ? 'invoice' : 'invoices'}
        accent
        onClick={onOpenOutstanding}
        clickHint="Open list"
      />
    </div>
  );
}

function StatCount({ icon, label, count, unit, accent, onClick, clickHint }) {
  // Clickable variant becomes a button so it's keyboard + screen-reader friendly.
  const className = `quick-stat-card ${accent ? 'outstanding' : ''} ${onClick ? 'clickable' : ''}`;
  const inner = (
    <>
      <div className="stat-label">
        <span style={{ verticalAlign: 'middle', marginRight: 2 }}>{icon}</span>
        {label}
        {onClick && <span className="stat-click-hint">{clickHint || 'Open'}</span>}
      </div>
      <div className="stat-value">
        {count}
        <span className="stat-unit">{unit}</span>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <div className={className}>{inner}</div>;
}

// ─── Needs-your-attention panel ──────────────────────────────────────────
function HomeNeedsAttention({ items, customerById, onSchedule }) {
  if (items.total === 0) {
    return (
      <div className="attention-all-clear">
        <Check size={18} />
        <span>All caught up — nothing needs follow-up.</span>
      </div>
    );
  }

  return (
    <div className="attention-panel">
      <h3 className="attention-title">
        <AlertTriangle size={14} /> Needs your attention
      </h3>

      {items.needsScheduling.length > 0 && (
        <AttentionSection
          icon={<CalendarPlus size={13} />}
          title={`${items.needsScheduling.length} approved — needs scheduling`}
          subtitle="Customer said yes and there's no trip booked. Get it on the calendar."
        >
          {items.needsScheduling.map((i) => (
            <li key={i.job.id} className="attention-item">
              <Link to={`/customers/${i.job.customerId}`} className="attention-link">
                <span className="attention-name">
                  {customerById[i.job.customerId]?.name || '(deleted customer)'}
                </span>
                <span className="attention-desc">{i.job.description}</span>
              </Link>
              <span className="attention-age">{i.days === 0 ? 'new' : `${i.days}d`}</span>
              <button
                type="button"
                className="attention-action"
                onClick={() => onSchedule(i.job)}
                title="Put this on the calendar"
              >
                <CalendarPlus size={13} /> Schedule
              </button>
            </li>
          ))}
        </AttentionSection>
      )}

      {items.approved.length > 0 && (
        <AttentionSection
          icon={<Check size={13} />}
          title={`${items.approved.length} approved — ready to invoice`}
          subtitle="Customer said yes. Send the invoice when the work is done."
          good
        >
          {items.approved.map((i) => (
            <AttentionRow
              key={i.job.id}
              job={i.job}
              customerName={customerById[i.job.customerId]?.name}
              suffix={i.days === 0 ? 'new' : `${i.days}d`}
              actionKind="send-invoice"
            />
          ))}
        </AttentionSection>
      )}

      {items.drafts.length > 0 && (
        <AttentionSection
          icon={<FileText size={13} />}
          title={`${items.drafts.length} draft${items.drafts.length === 1 ? '' : 's'} not sent`}
          subtitle="Send the estimate so the customer can approve."
        >
          {items.drafts.map((i) => (
            <AttentionRow
              key={i.job.id}
              job={i.job}
              customerName={customerById[i.job.customerId]?.name}
              suffix={null}
              actionKind="send-estimate"
            />
          ))}
        </AttentionSection>
      )}

      {items.stale.length > 0 && (
        <AttentionSection
          icon={<Send size={13} />}
          title={`${items.stale.length} estimate${items.stale.length === 1 ? '' : 's'} sent 3+ days ago`}
          subtitle="No response — consider a follow-up text."
        >
          {items.stale.map((i) => (
            <AttentionRow
              key={i.job.id}
              job={i.job}
              customerName={customerById[i.job.customerId]?.name}
              suffix={`${i.days}d`}
              actionKind="resend-estimate"
            />
          ))}
        </AttentionSection>
      )}

      {items.overdue.length > 0 && (
        <AttentionSection
          icon={<AlertCircle size={13} />}
          title={`${items.overdue.length} invoice${items.overdue.length === 1 ? '' : 's'} unpaid 14+ days`}
          subtitle="Time to chase the payment."
          severe
        >
          {items.overdue.map((i) => (
            <AttentionRow
              key={i.job.id}
              job={i.job}
              customerName={customerById[i.job.customerId]?.name}
              suffix={`${i.days}d`}
              actionKind="resend-invoice"
            />
          ))}
        </AttentionSection>
      )}
    </div>
  );
}

function AttentionSection({ icon, title, subtitle, severe, good, children }) {
  const className = `attention-section ${severe ? 'severe' : ''} ${good ? 'good' : ''}`;
  return (
    <div className={className.trim()}>
      <div className="attention-section-head">
        {icon}
        <span className="attention-section-title">{title}</span>
      </div>
      {subtitle && <p className="attention-section-sub">{subtitle}</p>}
      <ul className="attention-list">{children}</ul>
    </div>
  );
}

// AttentionRow — one entry inside a Needs-Your-Attention section.
// Every action button lands on the customer's detail page — that's
// where Send / Resend / Mark Paid / Edit / Delete all live. The button
// label is contextual to the row's situation so the mechanic knows
// what they're heading there to do.
//
//   actionKind          label    →  destination
//   ------------------  -------  -----------------------
//   send-estimate       Send     →  /customers/{customerId}
//   send-invoice        Invoice  →  /customers/{customerId}
//   resend-estimate     Resend   →  /customers/{customerId}
//   resend-invoice      Resend   →  /customers/{customerId}
function AttentionRow({ job, customerName, suffix, actionKind }) {
  const labels = {
    'send-estimate':   'Send',
    'send-invoice':    'Invoice',
    'resend-estimate': 'Resend',
    'resend-invoice':  'Resend',
  };
  const label = labels[actionKind] || 'Open';

  return (
    <li className="attention-item">
      <Link to={`/customers/${job.customerId}`} className="attention-link">
        <span className="attention-name">{customerName || '(deleted customer)'}</span>
        <span className="attention-desc">{job.description}</span>
      </Link>
      {suffix && <span className="attention-age">{suffix}</span>}
      <Link
        to={`/customers/${job.customerId}`}
        className="attention-action"
        title={`Open the customer page to ${label.toLowerCase()}`}
      >
        {label} <ChevronRight size={12} />
      </Link>
    </li>
  );
}

// ─── Seven-day list row ───────────────────────────────────────────────────
function SevenDayRow({ date, today, jobs, customerById, onTap, onJobTap }) {
  const isToday = isSameDay(date, today);
  return (
    <button type="button" className={`seven-day-row ${isToday ? 'today' : ''}`} onClick={onTap}>
      <div className="seven-day-row-header">
        <span className="seven-day-heading">{dayHeading(date, today)}</span>
        <span className="seven-day-count">
          {jobs.length === 0 ? '—' : `${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`}
        </span>
      </div>
      {jobs.length === 0 ? (
        <div className="seven-day-empty">— No jobs scheduled —</div>
      ) : (
        <ul className="seven-day-jobs">
          {jobs.map((j) => {
            const running = isRunning(j);
            return (
              <li key={j.id} className={`seven-day-job ${running ? 'running' : ''}`}>
                <button
                  type="button"
                  className="seven-day-job-btn"
                  onClick={(e) => { e.stopPropagation(); onJobTap(j); }}
                  title={running ? 'Resume active job' : 'Open job'}
                >
                  <span className="mono small seven-day-job-time">{formatTimeOfDay(j._start)}</span>
                  <span className="seven-day-job-name">
                    {customerById[j.customerId]?.name || '(deleted)'}
                  </span>
                  <span className={`badge badge-${running ? 'approved' : (j.status || 'draft')} seven-day-job-status`}>
                    {running ? 'On the job' : (STATUS_TEXT[j.status] || 'Draft')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <ChevronRight size={16} className="seven-day-row-chevron" />
    </button>
  );
}

export { startOfDay, addDays, isSameDay, pad2, isoDate, toDate, formatTimeOfDay, daysBetween, dayHeading, formatPhone, useNowTick, NextJobHero, HomeStats, StatCount, HomeNeedsAttention, AttentionSection, AttentionRow, SevenDayRow };
